import { useEffect, useState, useMemo } from 'react';
import { sendScreenEvent, sendCustomEvent } from '@useblu/blu-lytics';

import { useNavigate, useParams } from 'react-router-dom';

import { getPaymentLinkById } from '@/services/api';
import {
  getInstallments,
  hasFailedAttempt,
  isExpired,
  isPaid,
  isSettled,
} from '@/utils/paymentLink';
import { clearIdPayCompleted, ID_PAY_COMPLETED_KEY } from '@/utils/idPay';

import { IUseSuccessReturn, IStatus } from './types';
import { ILinkProps } from '@/pages/Checkout/types/paymentLinkContextTypes';

const POLLING_INTERVAL_MS = 5000;
const MAX_POLLING_DURATION_MS = 5 * 60 * 1000;
const MAX_POLLING_ATTEMPTS = MAX_POLLING_DURATION_MS / POLLING_INTERVAL_MS;

const useSuccess = (): IUseSuccessReturn => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [getLinkData, setGetLinkData] = useState<ILinkProps | null>(null);
  const [hasCompleted, setHasCompleted] = useState<boolean>(false);
  const [pollingAttempts, setPollingAttempts] = useState<number>(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const hasTimedOut = pollingAttempts >= MAX_POLLING_ATTEMPTS;

  const handleOpenLink = () => navigate(`/${id}`);

  const handleGetLinkData = async () => {
    await getPaymentLinkById(id).then(response => {
      setLoading(false);
      const data = response.data.data;
      setGetLinkData(data);
      setLastUpdatedAt(new Date());

      if (isSettled(data)) {
        setHasCompleted(true);
      }
    });
  };

  const handleRefresh = () => {
    setPollingAttempts(0);
    handleGetLinkData();
  };

  const handleGetInstallmentValue = () => {
    const amount = getLinkData?.amount;
    const installments = getInstallments(getLinkData);

    if (!amount || !installments) return 0;

    return parseFloat(amount) / installments;
  };

  const linkUrl = `/${id}`;

  const content: IStatus = useMemo(() => {
    if (isPaid(getLinkData)) return 'confirmed';
    if (hasFailedAttempt(getLinkData)) return 'failed';

    return 'processing';
  }, [getLinkData]);

  const showIdentityNotice = useMemo(
    () =>
      content === 'processing' &&
      sessionStorage.getItem(ID_PAY_COMPLETED_KEY) === id &&
      !isExpired(getLinkData),
    [content, id, getLinkData],
  );

  useEffect(() => {
    if (content === 'confirmed' || content === 'failed') {
      clearIdPayCompleted();
    }
  }, [content]);

  useEffect(() => {
    handleGetLinkData();
  }, []);

  useEffect(() => {
    if (hasCompleted) {
      if (isPaid(getLinkData)) {
        sendCustomEvent('link_blu_checkout_success', {
          payment_type: 'credit',
          link_id: id,
          installments: getInstallments(getLinkData),
          client_uuid: getLinkData?.client_uuid,
          amount: getLinkData?.amount,
          authentication_type:
            getLinkData?.last_payment_link_intent?.checkout_type,
        });
      }

      return undefined;
    }

    if (hasTimedOut) return undefined;

    const interval = setInterval(() => {
      setPollingAttempts(current => current + 1);
      handleGetLinkData();
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [hasCompleted, hasTimedOut]);

  const logEvent = () => {
    if (localStorage._bl_init === 'init') {
      sendScreenEvent('link_blu_checkout_success_view');
      return;
    }

    setTimeout(() => {
      logEvent();
    }, 1000);
  };

  useEffect(() => {
    logEvent();
  }, []);

  return {
    loading,
    content,
    showIdentityNotice,
    getLinkData,
    linkUrl,
    hasTimedOut,
    lastUpdatedAt,
    handleOpenLink,
    handleRefresh,
    handleGetInstallmentValue,
  };
};

export default useSuccess;
